import type { TestEvent } from '../types/index.js';

export class TestDinoServerError extends Error {
  public readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'TestDinoServerError';
    this.code = code;
    Error.captureStackTrace(this, this.constructor);
  }
}

// HTTP 402 — organization pool exhausted at session creation.
export class QuotaExhaustedError extends TestDinoServerError {
  public readonly details: {
    planName: string;
    totalLimit: number;
    used: number;
    resetDate?: string;
  };

  constructor(
    message: string,
    details: {
      planName: string;
      totalLimit: number;
      used: number;
      resetDate?: string;
    }
  ) {
    super('QUOTA_EXHAUSTED', message);
    this.name = 'QuotaExhaustedError';
    this.details = details;
  }
}

// WebSocket NACK on run:begin — test count exceeds available quota.
export class QuotaExceededError extends TestDinoServerError {
  public readonly details: {
    planName: string;
    totalTests: number;
    remaining: number;
    used: number;
    total: number;
    resetDate?: string;
    canPartialSubmit: boolean;
    allowedCount: number;
    projectName?: string;
    projectLimit?: number;
    projectUsed?: number;
    projectBorrowed?: number;
  };

  constructor(
    message: string,
    details: {
      planName: string;
      totalTests: number;
      remaining: number;
      used: number;
      total: number;
      resetDate?: string;
      canPartialSubmit: boolean;
      allowedCount: number;
      projectName?: string;
      projectLimit?: number;
      projectUsed?: number;
      projectBorrowed?: number;
    }
  ) {
    super('QUOTA_EXCEEDED', message);
    this.name = 'QuotaExceededError';
    this.details = details;
  }
}

// HTTP 403 TOKEN_TYPE_NOT_ALLOWED — bearer is a td_pat_ or cookie JWT, not a td_api_ pipeline key.
export class InvalidTokenTypeError extends TestDinoServerError {
  constructor(message?: string) {
    super(
      'TOKEN_TYPE_NOT_ALLOWED',
      message ??
        'This endpoint requires a pipeline API key (td_api_...). ' +
          'Personal access tokens and session cookies are not accepted.'
    );
    this.name = 'InvalidTokenTypeError';
  }
}

export class ForbiddenAuthError extends TestDinoServerError {
  constructor(message?: string) {
    super('AUTH_FORBIDDEN', message ?? 'Authentication forbidden by server');
    this.name = 'ForbiddenAuthError';
  }
}

// HTTP 405 — serverUrl points at the wrong service.
export class ServerEndpointError extends TestDinoServerError {
  constructor(path: string) {
    super(
      'WRONG_AUTH_ENDPOINT',
      `Server rejected POST ${path} (405 Method Not Allowed). Verify serverUrl points at the data-handler service.`
    );
    this.name = 'ServerEndpointError';
  }
}

// HTTP 401 — invalid or expired token at authentication.
export class UnauthorizedError extends TestDinoServerError {
  constructor(message?: string) {
    super('UNAUTHORIZED', message ?? 'Authentication failed — invalid or expired token');
    this.name = 'UnauthorizedError';
  }
}

// Wraps any HTTP failure from HttpClient so callers can branch on error.status
// (typed) instead of digging into axios internals with unsafe casts.
export class HttpDeliveryError extends Error {
  public readonly status: number;

  constructor(status: number, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'HttpDeliveryError';
    this.status = status;
    Error.captureStackTrace(this, this.constructor);
  }
}

// 413 is permanent on the auth endpoint; on the events endpoint the chunker
// splits and retries before reaching this classifier.
// 409: the server refused a run:begin whose --split slot was already reported
// under this split-id (two runs sharing a split-id); retrying cannot change that.
const PERMANENT_HTTP_STATUSES = new Set([400, 401, 402, 403, 404, 405, 409, 413]);

export function isPermanentHttpStatus(status: number | undefined): boolean {
  return status !== undefined && PERMANENT_HTTP_STATUSES.has(status);
}

// `count` may exceed 1 when the halving path collapses N per-half floor errors
// into a single throw; the outer accounting must use it (not +1) or the discard
// counter under-reports when multiple oversize events share a sub-batch.
export class EventTooLargeError extends HttpDeliveryError {
  public readonly count: number;

  constructor(message: string, options?: { cause?: unknown; count?: number }) {
    super(413, message, options);
    this.name = 'EventTooLargeError';
    this.count = options?.count ?? 1;
  }
}

// Restore only `.failed` to the buffer's front; earlier sub-batches already
// acked and re-sending them would duplicate on the server.
export class PartialBatchError extends Error {
  public readonly failed: TestEvent[];
  public readonly succeeded: number;

  constructor(failed: TestEvent[], succeeded: number, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'PartialBatchError';
    this.failed = failed;
    this.succeeded = succeeded;
    Error.captureStackTrace(this, this.constructor);
  }
}

export type ServerError =
  | QuotaExhaustedError
  | QuotaExceededError
  | InvalidTokenTypeError
  | ForbiddenAuthError
  | ServerEndpointError
  | UnauthorizedError;

export function isServerError(error: unknown): error is ServerError {
  return error instanceof TestDinoServerError;
}

export function isQuotaError(error: unknown): error is QuotaExhaustedError | QuotaExceededError {
  return error instanceof QuotaExhaustedError || error instanceof QuotaExceededError;
}

// Ingestion's 402 for a refused run:begin carries only { projectId }: the run is
// blocked by the execution-quota gate, the same outcome as the auth endpoint's
// overLimit flag, not a session-pool exhaustion with plan/usage figures.
export function isBlockedRunError(error: unknown): error is QuotaExceededError {
  return error instanceof QuotaExceededError && (error.details as { total?: number } | undefined)?.total === undefined;
}
